import { useEffect, useRef, useState } from 'react';

function clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
}

interface CalculateProgressParams {
    elapsedMs: number;
    initialDurationMs: number;
    initialTarget: number;
    maxProgress: number;
    tailTimeConstantMs: number;
    easePower: number;
}

function calculateProgress({ elapsedMs, initialDurationMs, initialTarget, maxProgress, tailTimeConstantMs, easePower }: CalculateProgressParams) {
    if (elapsedMs <= 0) {
        return 0;
    }

    if (elapsedMs <= initialDurationMs) {
        const ratio = elapsedMs / initialDurationMs;
        const easedRatio = 1 - Math.pow(1 - ratio, easePower);
        return initialTarget * easedRatio;
    }

    const tailRatio = 1 - Math.exp(-(elapsedMs - initialDurationMs) / tailTimeConstantMs);
    return initialTarget + (maxProgress - initialTarget) * tailRatio;
}

interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
    isActive?: boolean;
    ariaLabel?: string;
    initialDurationMs?: number;
    initialTarget?: number;
    maxProgress?: number;
    tailTimeConstantMs?: number;
    easePower?: number;
    updateIntervalMs?: number;
    trackClassName?: string;
    fillClassName?: string;
}

export function ProgressBar({
    isActive = true,
    ariaLabel = 'Loading progress',
    initialDurationMs = 60_000,
    initialTarget = 80,
    maxProgress = 99.2,
    tailTimeConstantMs = 90_000,
    easePower = 1.6,
    updateIntervalMs = 250,
    className,
    trackClassName,
    fillClassName,
    ...props
}: ProgressBarProps) {
    const [progress, setProgress] = useState(0);
    const startedAtRef = useRef<number>(undefined);

    useEffect(() => {
        if (!isActive) {
            startedAtRef.current = undefined;
            setProgress(0);
            return;
        }

        if (startedAtRef.current === undefined) {
            startedAtRef.current = Date.now();
        }

        const clampedTarget = clamp(initialTarget, 0, 100);
        const clampedMax = clamp(Math.max(maxProgress, clampedTarget), 0, 100);
        const safeInitialDuration = Math.max(initialDurationMs, 1);
        const safeTailTime = Math.max(tailTimeConstantMs, 1);
        const safeEasePower = Math.max(easePower, 0.1);
        const safeInterval = Math.max(updateIntervalMs, 16);

        const updateProgress = () => {
            if (startedAtRef.current === undefined) {
                return;
            }

            const elapsedMs = Date.now() - startedAtRef.current;
            const nextProgress = calculateProgress({
                elapsedMs,
                initialDurationMs: safeInitialDuration,
                initialTarget: clampedTarget,
                maxProgress: clampedMax,
                tailTimeConstantMs: safeTailTime,
                easePower: safeEasePower,
            });

            setProgress(prev => Math.max(prev, nextProgress));
        };

        updateProgress();
        const intervalId = window.setInterval(updateProgress, safeInterval);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [isActive, initialDurationMs, initialTarget, maxProgress, tailTimeConstantMs, easePower, updateIntervalMs]);

    return (
        <div className={className ?? ''} {...props}>
            <div className={`h-2 w-full overflow-hidden rounded-full bg-muted/60 ${trackClassName ?? ''}`} role="progressbar" aria-label={ariaLabel} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.floor(progress)}>
                <div className={`h-full rounded-full bg-primary transition-[width] duration-300 ease-out ${fillClassName ?? ''}`} style={{ width: `${progress}%` }} />
            </div>
        </div>
    );
}
